// ФТУЕ.2: dev-демо страница для визуальной проверки туториал-оверлея.
// Не в меню, доступ только по URL `/tutorial-demo`.
//
// Прогоняет 5 шагов:
//   1. Coach в центре без таргета (intro)
//   2. Spotlight + Hand + Coach с одной кнопкой
//   3. Pulse без спотлайта + Coach сбоку
//   4. Drag-стрелка между двух точек
//   5. Coach с прогресс-баром
//
// Шаги переключаются по кнопке «Дальше» в coach-mark. SKIP закрывает
// демо целиком.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTutorialStore, type TutorialStep } from './store';

const STEPS: TutorialStep[] = [
  {
    id: 'demo-intro',
    coach: {
      title: 'Это демо туториал-оверлея',
      body: 'Прогон через 5 примитивов: спотлайт, палец, рамка, drag-стрелка, прогресс-бар.',
      showSkip: true,
      showNext: true
    }
  },
  {
    id: 'demo-spotlight',
    targetSel: '[data-demo="play-btn"]',
    spotlight: true,
    hand: true,
    coach: {
      title: 'Spotlight + hand',
      body: 'Затемнение + дырка + палец-указатель. Сделано через SVG-mask. Тап работает.',
      dir: 'top',
      showSkip: true,
      showNext: true
    }
  },
  {
    id: 'demo-pulse',
    targetSel: '[data-demo="info-btn"]',
    pulse: true,
    accent: 'mg',
    coach: {
      title: 'Pulse без спотлайта',
      body: 'Только неон-рамка вокруг таргета, остальной UI не блокируется. Цвет акцента — magenta.',
      dir: 'left',
      showSkip: true,
      showNext: true
    }
  },
  {
    id: 'demo-drag',
    targetSel: '[data-demo="from"]',
    dragToSel: '[data-demo="to"]',
    pulse: true,
    accent: 'amber',
    coach: {
      title: 'Drag-стрелка',
      body: 'Пунктирная линия с marching-ants анимацией от source к target. Для drag-обучения.',
      dir: 'bottom',
      showSkip: true,
      showNext: true
    }
  },
  {
    id: 'demo-progress',
    targetSel: '[data-demo="play-btn"]',
    spotlight: true,
    accent: 'liv',
    progress: { current: 5, total: 12 },
    coach: {
      title: 'Прогресс-бар',
      body: 'Тонкая полоса вверху + кнопка SKIP. Скрывается в туториал-бое чтобы не загромождать поле.',
      dir: 'top',
      showSkip: true,
      showNext: true,
      nextLabel: 'Закончить'
    }
  }
];

export function TutorialDemo() {
  const navigate = useNavigate();
  const start = useTutorialStore(s => s.start);
  const stop  = useTutorialStore(s => s.stop);

  const runStep = (i: number) => {
    if (i >= STEPS.length) {
      stop();
      return;
    }
    start(STEPS[i], {
      onNext: () => runStep(i + 1),
      onSkip: () => stop()
    });
  };

  // Автостарт демо при загрузке страницы.
  useEffect(() => {
    runStep(0);
    return () => stop();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#07070B',
      color: '#F4F6FB',
      fontFamily: 'var(--f-body, Inter, system-ui)',
      padding: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 24
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, borderBottom: '1px solid rgba(160,170,200,.18)', paddingBottom: 12 }}>
        <h1 style={{ fontFamily: 'var(--f-display, Chakra Petch)', margin: 0, fontSize: 22, letterSpacing: '0.04em' }}>TUTORIAL · DEMO</h1>
        <button
          onClick={() => navigate('/home')}
          style={{
            marginLeft: 'auto',
            padding: '6px 10px',
            background: 'transparent',
            border: '1px solid rgba(160,170,200,.20)',
            color: 'var(--t-md, #A0A4B4)',
            fontFamily: 'var(--f-mono, JetBrains Mono)',
            fontSize: 11,
            letterSpacing: '0.10em',
            cursor: 'pointer'
          }}
        >
          ← HOME
        </button>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <button
          data-demo="play-btn"
          style={demoBtnPrimary}
          onClick={() => alert('Это таргет — на него фокусируется спотлайт')}
        >
          ⚔ НАЙТИ БОЙ
        </button>
        <button
          data-demo="info-btn"
          style={demoBtn}
        >
          ⓘ ИНФО
        </button>
      </div>

      <div style={{ marginTop: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div
          data-demo="from"
          style={{ ...demoCard, borderColor: 'rgba(255, 138, 31, 0.50)' }}
        >
          <div style={{ fontSize: 11, fontFamily: 'var(--f-mono)', color: 'var(--t-md)' }}>SOURCE</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>Карта в руке</div>
        </div>
        <div
          data-demo="to"
          style={{ ...demoCard, borderColor: 'rgba(255, 138, 31, 0.50)', marginTop: 120 }}
        >
          <div style={{ fontSize: 11, fontFamily: 'var(--f-mono)', color: 'var(--t-md)' }}>TARGET</div>
          <div style={{ fontSize: 14, marginTop: 4 }}>Линия на поле</div>
        </div>
      </div>

      <div style={{
        marginTop: 'auto',
        padding: 14,
        fontFamily: 'var(--f-mono, JetBrains Mono)',
        fontSize: 11,
        color: 'var(--t-lo, #60657A)',
        letterSpacing: '0.06em',
        background: 'rgba(255,255,255,.02)',
        border: '1px dashed rgba(160,170,200,.18)'
      }}>
        <div>data-demo="play-btn" — таргет для шагов 1, 4</div>
        <div>data-demo="info-btn" — таргет для шага 2 (pulse, magenta)</div>
        <div>data-demo="from" → "to" — drag-стрелка, шаг 3 (amber)</div>
        <div>Каждый шаг — отдельная конфигурация TutorialStep в STEPS[].</div>
      </div>
    </div>
  );
}

const demoBtnPrimary: React.CSSProperties = {
  padding: '14px 26px',
  background: 'var(--neon-cy, #3DF0FF)',
  color: '#07070B',
  fontFamily: 'var(--f-display, Chakra Petch)',
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: '0.10em',
  textTransform: 'uppercase',
  border: 'none',
  cursor: 'pointer'
};

const demoBtn: React.CSSProperties = {
  padding: '14px 22px',
  background: 'transparent',
  color: 'var(--t-md, #A0A4B4)',
  fontFamily: 'var(--f-display, Chakra Petch)',
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '0.10em',
  textTransform: 'uppercase',
  border: '1px solid rgba(160,170,200,.30)',
  cursor: 'pointer'
};

const demoCard: React.CSSProperties = {
  padding: '16px 20px',
  background: 'var(--bg-2, #101018)',
  border: '1px solid',
  minWidth: 140
};
